/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CircleDollarSign } from 'lucide-react';
import { ListItemCard, Text } from '@/shared/ui';
import { BoardColumn } from '@/shared/ui/components/basic/board';
import { formatStatsCurrency } from '@/shared/utils/format-stats';
import {
  SALE_STATE_STYLES,
  groupSalesByState,
  useSaleBoardColumnLabel,
} from '../constants/sale-board-columns';
import type { SaleState } from '../constants/sale-states';
import { pendingMilestones } from '../constants/sale-triage';
import { useSalePaperworkMilestoneTypes } from '../hooks/useSalePaperworkMilestoneTypes';
import type { Sale, SalePaperworkMilestoneType } from '../types/sale.types';

export interface SaleBoardProps {
  agencyId: string;
  /** The sales the page already filtered and searched. */
  sales: readonly Sale[];
}

/**
 * One sale on the board: the buyer, the property, and the agreed value.
 *
 * The value is the card's third line rather than a fourth column of prose
 * because it is what distinguishes two sales of the same building — and
 * unlike the lease board there is no state badge competing with it, the
 * column already being the state.
 */
function SaleBoardCard({ agencyId, sale, milestoneTypes }: {
  agencyId: string;
  sale: Sale;
  milestoneTypes: SalePaperworkMilestoneType[];
}) {
  const { t } = useTranslation('features/sales');
  const navigate = useNavigate();
  const accent = SALE_STATE_STYLES[sale.state as SaleState];
  const ordered = useMemo(
    () => [...milestoneTypes].sort((a, b) => a.order - b.order),
    [milestoneTypes],
  );
  const pending = pendingMilestones(sale, milestoneTypes);

  return (
    <div data-testid="sale-board-card" data-state={sale.state}>
      <ListItemCard
        accentColor={accent?.color}
        firstName={sale.buyer_name || t('board.card.unnamedBuyer')}
        description={sale.property_title || sale.property_id || t('board.card.noProperty')}
        descriptionClamp={2}
        onClick={() => navigate(`/app/agencies/${agencyId}/sales/${sale.id}`)}
      >
        {/* A segment per configured milestone, filled by the dates the
          * record carries. The optional ones get a segment too — they just
          * do not gate completeness, which is why the count beside it reads
          * only the required set. */}
        {ordered.length > 0 && (
          <div data-testid="sale-card-milestones" className="flex w-full items-center gap-(--space-1)">
            {ordered.map((type) => (
              <div
                key={type.id}
                title={type.label}
                className="h-1 flex-1 rounded-full"
                style={{
                  background: sale.paperwork?.[type.id]
                    ? accent?.color ?? 'var(--accent-mostarda)'
                    : 'var(--border-subtle)',
                }}
              />
            ))}
          </div>
        )}

        <div className="flex w-full flex-wrap items-center gap-(--space-2)">
          {/* Only an open sale can be missing something. The backend
            * empties `paperwork_pending` on a terminal one, so naming a
            * gap there would describe work that no longer exists. */}
          {sale.state === 'in_paperwork' && pending.length > 0 && (
            <Text
              component="span"
              data-testid="sale-card-pending"
              className="text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] text-(--accent-mostarda)"
            >
              {t('board.card.missing', { milestone: pending[0].label })}
            </Text>
          )}
          <Text
            component="span"
            data-testid="sale-board-card-value"
            className="ml-auto tabular-nums text-[length:var(--text-meta)] [font-weight:var(--font-bold)] text-(--text-primary)"
          >
            {formatStatsCurrency(sale.accepted_value)}
          </Text>
        </div>
      </ListItemCard>
    </div>
  );
}

/**
 * Vendas as a board: one column per state.
 *
 * Unpaged, like the lease board and for the same reason — the page fetches
 * every sale of the agency in one call, so a column asking for more would
 * have nothing to ask, and a hidden remainder would make its own header
 * count a lie.
 */
export function SaleBoard({ agencyId, sales }: SaleBoardProps) {
  const { t } = useTranslation('features/sales');
  const columnLabel = useSaleBoardColumnLabel();
  const columns = useMemo(() => groupSalesByState(sales), [sales]);
  const { types: milestoneTypes } = useSalePaperworkMilestoneTypes(agencyId);

  /*
   * Only the in-paperwork column can owe a milestone: the backend empties
   * `paperwork_pending` once a sale concludes or is voided, so a warning
   * over a terminal column would be about work that no longer exists.
   *
   * Silent at zero, and silent while the types are still loading — a
   * count taken before they arrive reads every sale as complete.
   */
  const alertFor = (state: string, rows: readonly Sale[]): string | undefined => {
    if (state !== 'in_paperwork' || milestoneTypes.length === 0) return undefined;
    const pending = rows.filter((sale) => pendingMilestones(sale, milestoneTypes).length > 0).length;
    return pending > 0 ? t('board.column.pending', { count: pending }) : undefined;
  };

  return (
    <div
      data-testid="sale-board"
      className="flex flex-col gap-(--space-5) md:grid md:items-start md:gap-(--space-4)"
      style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
    >
      {columns.map((column) => (
        <BoardColumn
          key={column.state}
          id={column.state}
          label={columnLabel(column.state)}
          count={column.sales.length}
          accent={SALE_STATE_STYLES[column.state]}
          emptyIcon={(
            <CircleDollarSign
              width={18}
              height={18}
              style={{ color: 'var(--text-tertiary)', opacity: 0.6 }}
            />
          )}
          emptyLabel={t('board.column.empty')}
          isEmpty={column.sales.length === 0}
          alert={alertFor(column.state, column.sales)}
        >
          {column.sales.map((sale) => (
            <SaleBoardCard
              key={sale.id}
              agencyId={agencyId}
              sale={sale}
              milestoneTypes={milestoneTypes}
            />
          ))}
        </BoardColumn>
      ))}
    </div>
  );
}

export default SaleBoard;
